import { deleteCsrfCookie, forceDeleteCsrfCookie } from '../../../application/csrfProtection/clearCookies';
import { ILocalization, ILocalizationToken } from '../../../application/language/ILocalization';
import { extensionContainer } from '../../../common/container/ContainerContext';
import { inject } from '../../../common/container/inject';
import { IRestService, RestServiceToken } from '../../common/rest/IRestService';
import { isUnauthorizedRedirectInProgress } from '../../common/rest/handleUnauthorizedError';
import { WrongCredentialError } from './error/WrongCredentialError';
import { IActiveUserModel } from './IActiveUserModel';
import { IActiveUserService } from './IActiveUserService';

export class RestActiveUserService implements IActiveUserService {
  private unloggedActiveUser: IActiveUserModel = {
    allowedPermissions: [],
    isLogged: false,
    avatarUrl: '',
    firstName: '',
    lastName: '',
    id: '',
    isLoggedState: false,
    newMessagesCount: 0,
    roleId: '',
    roleName: '',
  };

  constructor(protected restService: IRestService = inject<IRestService>(RestServiceToken)) {}

  async getActiveUser(): Promise<IActiveUserModel> {
    let result = this.unloggedActiveUser;

    try {
      const userData = this.restService.get(this.makeUrl('/user/active/data')).catch(this.errorHandler);

      if(!userData) {
        return this.unloggedActiveUser;
      }

      const userDataBody = await userData

      result = {
        isLogged: true,
        avatarUrl: '',
        firstName: userDataBody.displayName,
        lastName: '',
        id: userDataBody.id,
        isLoggedState: true,
        allowedPermissions: userDataBody.allowedPermissions || [],
        newMessagesCount: userDataBody.newMessagesCount,
        roleName: userDataBody.roleName || '',
        roleId: userDataBody.roleName || '',
        email: userDataBody.email || '',
        directives: userDataBody.directives || {}
      };
    } catch (error) {
      if (!isUnauthorizedRedirectInProgress()) {
        await this.logoutActiveUser();
      }
    }

    return result;
  }

  async loginUserByPassword(login: string, password: string, loginUserByPassword: string | null): Promise<boolean> {
    await this.restService
      .post< any, any>(this.makeUrl('/user/active/login'),
        { login, password, adServerId: loginUserByPassword }
  ).catch(this.errorHandler);;

    return true;
  }

  async updatePassword(password: string): Promise<boolean> {
    try {
      await this.restService.put<any, any>(this.makeUrl(''), { password });
    } catch (error) {
      this.errorHandler(error);
    }

    return true;
  }

  async refreshTokenFunc(): Promise<boolean> {
    try {
      await this.restService.post(this.makeUrl('/user/active/refresh'), {});
      return true;
    } catch {
      return false;
    }
  }

  async logoutActiveUser(): Promise<boolean> {
    try {
      await this.restService.post<any, any>(this.makeUrl('/user/active/logout'), {});
      const cookieDeleted = deleteCsrfCookie();
      if (!cookieDeleted) {
        //console.warn('CSRF cookie deletion failed, trying force delete...');
        forceDeleteCsrfCookie();
      }
    } catch (error) {}
    return true;
  }

  makeUrl(uri: string) {
    const domain = this.restService.getConfig().url.api.public;
    return `${domain}${uri}`;
  }

  errorHandler = (error: any) => {
    if (String(error?.webCode ?? '') === '404') {
      const i18n: ILocalization = extensionContainer.getByToken(ILocalizationToken);
      throw new WrongCredentialError([
        {
          message: i18n.translate('login.incorrectValid'),
          target: 'login',
        },
      ]);
    } else {
      throw error;
    }
  };
}
